import { Button, CircularProgress, Typography } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { Alert } from "@material-ui/lab";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import axios from "../../axiosCF";
import PendingFiles from "../../components/PendingFiles/PendingFiles";
import CustomDialog from "../../components/UI/CustomDialog/CustomDialog";
import Input, { EInputType } from "../../components/UI/Input/Input";
import {
  academyOptions,
  educationContentOptions,
  targetGroupOptions,
} from "../../data/academy";
import withErrorHandler from "../../hoc/withErrorHandler/withErrorHandler";
import { IAcademyEducation, IUser, SUEK_TOPIC_OPTIONS } from "../../interfaces/domain";
import { IAppState } from "../../interfaces/state";
import {
  defaultInputChangedHandler,
  getFormData,
  getInvalidControlLabels,
  IControls,
  IInputConfigOptional,
  touchAllControls,
  validateInput,
} from "../../shared/utility";
import * as actions from "../../store/actions";

import classes from "./AcademyEducation.module.scss";

export enum EFields {
  name = "name",
  instructor = "instructor",
  instructorEmail = "instructorEmail",
  educationDate = "educationDate",
  academy = "academy",
  targetGroups = "targetGroups",
  targetGroupOther = "targetGroupOther",
  participantsAmount = "participantsAmount",
  educationContents = "educationContents",
  educationContentDescription = "educationContentDescription",
  topics = "topics",
  feedback = "feedback",
  comments = "comments",
  attachments = "attachments",
  implementationAndMaterials = "implementationAndMaterials",
  infoSession = "infoSession",
}

const initControls: IControls = {
  controls: {
    [EFields.name]: {
      type: EInputType.text,
      value: "",
      label: "Koulutuksen nimi",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.instructor]: {
      type: EInputType.text,
      value: "",
      label: "Kouluttaja",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.instructorEmail]: {
      type: EInputType.email,
      value: "",
      label: "Sähköposti",
      validation: {
        required: true,
        email: true,
      },
      valid: false,
    },
    [EFields.educationDate]: {
      type: EInputType.date,
      value: null,
      label: "Koulutuksen päivämäärä",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.academy]: {
      type: EInputType.select,
      value: [],
      label: "Urheiluopisto",
      options: academyOptions,
      multiple: true,
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.targetGroups]: {
      type: EInputType.select,
      value: [],
      label: "Kohderyhmät",
      options: targetGroupOptions,
      multiple: true,
      validation: {
        required: true,
        or: ["targetGroupOther"],
      },
      valid: false,
    },
    [EFields.targetGroupOther]: {
      type: EInputType.text,
      value: "",
      label: "Muu kohderyhmä",
      validation: {
        required: true,
        or: ["targetGroups"],
      },
      valid: false,
    },
    [EFields.participantsAmount]: {
      type: EInputType.number,
      value: "",
      label: "Osallistujamäärä",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.educationContents]: {
      type: EInputType.select,
      value: [],
      label: "Koulutuksen sisältö",
      options: educationContentOptions,
      multiple: true,
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.educationContentDescription]: {
      type: EInputType.text,
      value: "",
      label: "Kuvaus koulutuksen sisällöstä",
      multiline: true,
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.topics]: {
      type: EInputType.select,
      value: [],
      label: "Valitse kaikki koulutuksessa läpikäydyt aihealueet",
      multiple: true,
      options: SUEK_TOPIC_OPTIONS,
      valid: true,
    },
    [EFields.feedback]: {
      type: EInputType.text,
      value: "",
      label: "Palaute",
      multiline: true,
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.comments]: {
      type: EInputType.text,
      value: "",
      label: "Muita kommentteja",
      multiline: true,
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.attachments]: {
      type: EInputType.dropzone,
      value: "",
      label: "Liitteet",
      validation: {
        required: true,
        or: [EFields.implementationAndMaterials],
      },
      valid: false,
    },
    [EFields.implementationAndMaterials]: {
      type: EInputType.text,
      value: "",
      label: "Kuvaa tarkasti koulutuksen toteutustapa ja käytettyjä materiaaleja",
      multiline: true,
      validation: {
        required: true,
        or: [EFields.attachments],
      },
      valid: false,
    },
    [EFields.infoSession]: {
      type: EInputType.checkbox,
      value: [],
      options: [{ value: "Y", text: "Tietoisku n.15 min" }],
      label: "Tietoisku n.15 min",
      validation: {
        required: false
      },
      valid: true,
    },
  },
  isValid: false,
};

interface IDispatchProps {
  onSaveAcademyEducation: (formData: FormData) => Promise<IAcademyEducation>;
}

interface IStateProps {
  loading: boolean;
  user: IUser | null;
}

interface IProps extends IDispatchProps, IStateProps {}

const AcademyEducation: React.FC<IProps> = ({
  onSaveAcademyEducation,
  user,
  loading,
}) => {
  const [controls, setControls] = useState(initControls);
  const [isOpen, setOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const applyUserDefaults = useCallback(
    (baseControls: IControls, currentUser: IUser | null): IControls => {
      if (!currentUser) {
        return baseControls;
      }
      let updatedControls = baseControls;
      if (currentUser.name) {
        updatedControls = validateInput(
          updatedControls.controls,
          EFields.instructor,
          currentUser.name,
          false,
        );
      }
      if (currentUser.email) {
        updatedControls = validateInput(
          updatedControls.controls,
          EFields.instructorEmail,
          currentUser.email,
          false,
        );
      }
      return updatedControls;
    },
    [],
  );

  useEffect(() => {
    if (user !== null) {
      setControls((prevControls) => applyUserDefaults(prevControls, user));
    }
  }, [user, applyUserDefaults]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!controls.isValid) {
      setSubmitAttempted(true);
      setControls((prevControls) => touchAllControls(prevControls.controls));
      return;
    }

    const controlsData = getFormData(controls.controls);

    const formData = new FormData();
    if (pendingFiles && pendingFiles.length > 0) {
      pendingFiles.forEach((file, index) => {
        formData.append(`file_${index + 1}`, file);
      });
      formData.append("fileAmount", pendingFiles.length.toString());
    }
    formData.append("data", JSON.stringify(controlsData));

    const academyEducation = await onSaveAcademyEducation(formData);
    if (academyEducation && academyEducation.id) {
      setOpen(true);
    }
  };

  const onResetForm = () => {
    setSubmitAttempted(false);
    setControls(applyUserDefaults(initControls, user));
    setPendingFiles([]);
  };

  const onCloseDialog = () => {
    setOpen(false);
    onResetForm();
  };

  useEffect(() => {
    const attachmentsValue = pendingFiles.length > 0 ? "has_files" : "";
    setControls((prevControls) => {
      if (prevControls.controls[EFields.attachments].value === attachmentsValue) {
        return prevControls;
      }
      return validateInput(
        prevControls.controls,
        EFields.attachments,
        attachmentsValue,
        false,
      );
    });
  }, [pendingFiles]);

  const dropHandler = useCallback((files: File[]) => {
    setPendingFiles((prevFiles) => {
      const uniqueNewFiles = files.filter(
        (file, idx, self) =>
          !prevFiles.some(
            (existing) =>
              existing.name === file.name && existing.size === file.size,
          ) &&
          self.findIndex(
            (f) => f.name === file.name && f.size === file.size,
          ) === idx,
      );
      return [...prevFiles, ...uniqueNewFiles];
    });
  }, []);

  const removePendingFileHandler = useCallback((index: number) => {
    setPendingFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const createInput = (inputName: EFields, config?: IInputConfigOptional) => {
    return (
      <Input
        formControl={{
          id: inputName,
          config: {
            ...controls.controls[inputName],
            ...config,
          },
        }}
        changed={(value, controlName: string) =>
          defaultInputChangedHandler(value, controlName, setControls)
        }
      />
    );
  };

  const invalidLabels =
    submitAttempted && !controls.isValid
      ? getInvalidControlLabels(controls.controls)
      : [];

  return (
    <React.Fragment>
      <CustomDialog
        title="Kiitos"
        content="Olemme vastaanottaneet koulutuksen tiedot, kiitos."
        buttonText="Sulje"
        onOk={onCloseDialog}
        open={isOpen}
      />
      <Paper elevation={4} className={classes.Paper}>
        <Typography variant="h5" gutterBottom>
          Urheiluopistojen järjestämät koulutukset
        </Typography>
        <Typography variant="subtitle1">
          Täytä lomakkeen kaikki kohdat ja paina Lähetä. Tähdellä (*) merkityt
          kentät ovat pakollisia.
        </Typography>
        <form onSubmit={onSubmit} noValidate>
          {createInput(EFields.name)}
          {createInput(EFields.instructor)}
          {createInput(EFields.instructorEmail)}
          {createInput(EFields.educationDate)}
          {createInput(EFields.academy)}
          {createInput(EFields.targetGroups)}
          {createInput(EFields.targetGroupOther)}
          {createInput(EFields.participantsAmount)}
          {createInput(EFields.educationContents)}
          {createInput(EFields.educationContentDescription)}
          {createInput(EFields.topics)}
          {createInput(EFields.infoSession)}
          {createInput(EFields.feedback)}
          {createInput(EFields.comments)}

          <p>
            Lataa aina käyttämäsi koulutusmateriaali tai mikäli et käyttänyt diaesitystä, kuvaa alle tarkasti koulutuksen toteutustapa
          </p>
          {createInput(EFields.attachments, { onDropHandler: dropHandler })}
          <PendingFiles
            pendingFiles={pendingFiles}
            onRemove={removePendingFileHandler}
          />
          {createInput(EFields.implementationAndMaterials)}

          {submitAttempted && !controls.isValid && invalidLabels.length > 0 && (
            <Alert severity="error" className={classes.Alert}>
              Täytä tai tarkista vielä seuraavat pakolliset kentät:{" "}
              <strong>{invalidLabels.join(", ")}</strong>
            </Alert>
          )}

          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Button
                color="primary"
                className={classes.Button}
                type="submit"
              >
                Lähetä
              </Button>

              <Button className={classes.Button} onClick={onResetForm}>
                Tyhjennä
              </Button>
            </>
          )}
        </form>
      </Paper>
    </React.Fragment>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => {
  return {
    loading: state.academy.loading,
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: any): IDispatchProps => {
  return {
    onSaveAcademyEducation: (academyEducation) =>
      dispatch(actions.saveAcademyEducation(academyEducation)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withErrorHandler<IProps>(AcademyEducation, axios));
